// 計画ルートからの逸脱を検出し、警告を出す
//
// 距離計算: 短距離なので球面三角ではなく平面近似(緯度毎の m/度を使う)
// 警告レベル:
//   ok    : <=50m
//   warn  : 50-100m
//   danger: >100m
//
// 通知方法: バイブ + Web Audio ビープ + 画面バナー
// 通知間隔: 同レベル維持中は再通知しない、レベル変化時に30秒以上経過していれば再通知

const THRESHOLD_WARN = 50;
const THRESHOLD_DANGER = 100;
const NOTIFY_INTERVAL_MS = 30 * 1000;

let warnState = { level: 'ok', lastNotifyTs: 0 };
let audioCtx = null;

// 点 P から線分 A-B への最短距離 (m)
function distancePointToSegment(p, a, b) {
  const lat0 = (a.lat + b.lat) / 2;
  const mPerDegLat = 111320;
  const mPerDegLng = 111320 * Math.cos((lat0 * Math.PI) / 180);
  const ax = (a.lng - p.lng) * mPerDegLng;
  const ay = (a.lat - p.lat) * mPerDegLat;
  const bx = (b.lng - p.lng) * mPerDegLng;
  const by = (b.lat - p.lat) * mPerDegLat;
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(ax, ay);
  let t = -(ax * dx + ay * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const px = ax + t * dx;
  const py = ay + t * dy;
  return Math.hypot(px, py);
}

export function nearestRouteDistance(lat, lng, waypoints) {
  if (!waypoints || waypoints.length < 2) return null;
  let min = Infinity;
  const cur = { lat, lng };
  for (let i = 1; i < waypoints.length; i++) {
    const d = distancePointToSegment(cur, waypoints[i - 1], waypoints[i]);
    if (d < min) min = d;
  }
  return min;
}

// 警告レベルの判定 + (必要なら)通知発火
//   返却: { level, distance }
export function checkRouteDeviation(lat, lng, waypoints) {
  const d = nearestRouteDistance(lat, lng, waypoints);
  if (d === null) return { level: 'ok', distance: null };

  let level = 'ok';
  if (d > THRESHOLD_DANGER) level = 'danger';
  else if (d > THRESHOLD_WARN) level = 'warn';

  const now = Date.now();
  const levelChanged = level !== warnState.level;

  if (level !== 'ok' && levelChanged && now - warnState.lastNotifyTs > NOTIFY_INTERVAL_MS / 3) {
    notify(level);
    warnState.lastNotifyTs = now;
  }
  warnState.level = level;
  return { level, distance: d };
}

export function resetDeviationState() {
  warnState = { level: 'ok', lastNotifyTs: 0 };
}

function notify(level) {
  if (navigator.vibrate) {
    if (level === 'danger') navigator.vibrate([300, 100, 300, 100, 300]);
    else navigator.vibrate([200, 100, 200]);
  }
  beep(level);
}

function beep(level) {
  if (!audioCtx) {
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      return;
    }
  }
  const ctx = audioCtx;
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = level === 'danger' ? 880 : 660;
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
    osc.start();
    osc.stop(ctx.currentTime + 0.35);
  } catch (e) {
    /* noop */
  }
}
